import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eraser, PenTool, Type, CheckCircle2, AlertCircle } from "lucide-react";
import { validateSignature, describeReason, serializeStrokes } from "@/lib/signatureValidate";

const TYPE_FONTS = [
    { id: "Caveat", label: "Caveat", style: { fontFamily: "Caveat, cursive" } },
    { id: "Dancing Script", label: "Dancing Script", style: { fontFamily: "'Dancing Script', cursive" } },
    { id: "Great Vibes", label: "Great Vibes", style: { fontFamily: "'Great Vibes', cursive" } },
];

let fontsInjected = false;
function ensureFonts() {
    if (fontsInjected || typeof document === "undefined") return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&display=swap";
    document.head.appendChild(link);
    fontsInjected = true;
}

/**
 * Stand-alone signature pad for CONTRACT signing (Phase 6A).
 * Phase SIG-VALIDATE — strict 4-rule validation + live indicator.
 * Returns the payload to the parent — does not POST itself.
 */
export function ContractSignaturePad({ defaultSignerName = "", onSign, busy, ctaLabel = "Sign contract", testIdPrefix = "contract-sign", extraFields = null, canSubmit = true }) {
    const [mode, setMode] = useState("draw");
    const [signerName, setSignerName] = useState(defaultSignerName);
    const [typedText, setTypedText] = useState(defaultSignerName);
    const [font, setFont] = useState(TYPE_FONTS[0].id);
    const [submitError, setSubmitError] = useState(null);
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const segmentsRef = useRef([]);
    const startTsRef = useRef(null);
    const [validation, setValidation] = useState({ ok: false, reason: "empty", metrics: {} });

    useEffect(() => { ensureFonts(); }, []);

    const ctx = () => canvasRef.current?.getContext("2d");
    const sizeCanvas = () => {
        const c = canvasRef.current;
        if (!c) return;
        const ratio = window.devicePixelRatio || 1;
        const rect = c.getBoundingClientRect();
        c.width = rect.width * ratio;
        c.height = rect.height * ratio;
        const g = c.getContext("2d");
        g.scale(ratio, ratio);
        g.lineWidth = 2;
        g.lineCap = "round";
        g.strokeStyle = "#0f172a";
    };

    useEffect(() => {
        if (mode === "draw") {
            requestAnimationFrame(sizeCanvas);
            segmentsRef.current = [];
            startTsRef.current = null;
            setValidation({ ok: false, reason: "empty", metrics: {} });
            setSubmitError(null);
        }
    }, [mode]);

    const pos = (e) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    };

    const pushPoint = (p) => {
        const segs = segmentsRef.current;
        if (!segs.length) return;
        const ts = startTsRef.current == null ? 0 : (performance.now() - startTsRef.current);
        segs[segs.length - 1].push({ x: p.x, y: p.y, t: ts });
        setValidation(validateSignature({ segments: segs, method: "draw" }));
    };

    const start = (e) => {
        drawing.current = true;
        const { x, y } = pos(e);
        const g = ctx();
        g.beginPath();
        g.moveTo(x, y);
        if (startTsRef.current == null) startTsRef.current = performance.now();
        segmentsRef.current.push([]);
        pushPoint({ x, y });
    };
    const move = (e) => {
        if (!drawing.current) return;
        const { x, y } = pos(e);
        const g = ctx();
        g.lineTo(x, y);
        g.stroke();
        pushPoint({ x, y });
    };
    const end = () => { drawing.current = false; };
    const clear = () => {
        const c = canvasRef.current;
        if (!c) return;
        const g = c.getContext("2d");
        g.clearRect(0, 0, c.width, c.height);
        segmentsRef.current = [];
        startTsRef.current = null;
        setValidation({ ok: false, reason: "empty", metrics: {} });
        setSubmitError(null);
    };

    const drawValid = mode === "draw" ? validation.ok : (typedText || "").trim().length > 0;
    const disabledSubmit = busy || !signerName.trim() || !drawValid || !canSubmit;

    const submit = () => {
        if (!signerName.trim()) return;
        if (mode === "draw") {
            const v = validateSignature({ segments: segmentsRef.current, method: "draw" });
            setValidation(v);
            if (!v.ok) {
                setSubmitError(describeReason(v.reason));
                return;
            }
            const dataUrl = canvasRef.current.toDataURL("image/png");
            setSubmitError(null);
            onSign({
                signer_name: signerName.trim(),
                method: "draw",
                image_data_url: dataUrl,
                stroke_data: serializeStrokes(segmentsRef.current),
            });
        } else {
            if (!typedText.trim()) return;
            setSubmitError(null);
            onSign({ signer_name: signerName.trim(), method: "type", typed_text: typedText.trim(), font });
        }
    };

    return (
        <div className="space-y-4" data-testid={`${testIdPrefix}-pad`}>
            <div className="flex gap-2">
                <Button type="button" size="sm" variant={mode === "draw" ? "default" : "outline"}
                    onClick={() => setMode("draw")} className="gap-1.5"
                    data-testid={`${testIdPrefix}-mode-draw`}><PenTool size={14} /> Draw</Button>
                <Button type="button" size="sm" variant={mode === "type" ? "default" : "outline"}
                    onClick={() => setMode("type")} className="gap-1.5"
                    data-testid={`${testIdPrefix}-mode-type`}><Type size={14} /> Type</Button>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor={`${testIdPrefix}-name`}>Full legal name</Label>
                <Input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name`} value={signerName}
                    onChange={(e) => setSignerName(e.target.value)} placeholder="Type your name" />
            </div>

            {mode === "draw" ? (
                <div>
                    <div className="rounded-md border border-slate-200 bg-white relative overflow-hidden">
                        <canvas ref={canvasRef} className="block w-full h-40 touch-none cursor-crosshair"
                            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                            onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid={`${testIdPrefix}-canvas`} />
                        <Button type="button" variant="ghost" size="sm" onClick={clear}
                            className="absolute top-2 right-2 gap-1.5 text-slate-500"
                            data-testid={`${testIdPrefix}-clear`}><Eraser size={14} /> Clear</Button>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                        {validation.ok ? (
                            <span data-testid={`${testIdPrefix}-live-valid`}
                                className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                                <CheckCircle2 size={12} /> Valid signature
                            </span>
                        ) : (
                            <span data-testid={`${testIdPrefix}-live-invalid`}
                                className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                                <AlertCircle size={12} /> {validation.reason === "empty" ? "Draw to begin" : "Needs more detail"}
                            </span>
                        )}
                        <p className="text-xs text-slate-400">Sign with mouse, finger, or stylus.</p>
                    </div>
                    {submitError && (
                        <p data-testid={`${testIdPrefix}-submit-error`}
                            className="mt-1.5 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5">
                            {submitError}
                        </p>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Choose a script</Label>
                        <div className="grid grid-cols-3 gap-2">
                            {TYPE_FONTS.map((f) => (
                                <button key={f.id} type="button" onClick={() => setFont(f.id)}
                                    data-testid={`${testIdPrefix}-font-${f.id}`}
                                    className={`rounded-md border px-3 py-2 text-2xl ${font === f.id ? "border-blue-900 bg-blue-50" : "border-slate-200"}`}
                                    style={f.style}>{(typedText || signerName) || f.label}</button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor={`${testIdPrefix}-typed`}>Typed signature</Label>
                        <Input id={`${testIdPrefix}-typed`} data-testid={`${testIdPrefix}-typed`} value={typedText}
                            onChange={(e) => setTypedText(e.target.value)} placeholder="Type your signature" />
                    </div>
                    {(typedText || signerName) && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-3xl text-slate-900"
                            style={TYPE_FONTS.find((f) => f.id === font)?.style}>{typedText || signerName}</div>
                    )}
                </div>
            )}

            {extraFields}

            <Button onClick={submit} disabled={disabledSubmit}
                className="bg-blue-900 hover:bg-blue-800 gap-1.5 w-full sm:w-auto"
                data-testid={`${testIdPrefix}-submit`}>
                <CheckCircle2 size={14} /> {busy ? "Submitting…" : ctaLabel}
            </Button>
        </div>
    );
}
